import { z } from 'zod'
import { type BrandingSetting, brandingSettingSchema } from './branding'
import { DEFAULT_INTEREST_AREAS } from './volunteer-interests'

/**
 * Typed settings registry. Values live in the `settings` table as JSON; anything not stored falls
 * back to the default. `public: true` settings are served to the SPA without authentication.
 * `hidden` settings have their own admin page and stay out of the generic settings editor.
 */
export interface SettingDef<T = unknown> {
  label: string
  help: string
  public: boolean
  schema: z.ZodType<T>
  default: T
  hidden?: boolean
}

const def = <T>(d: SettingDef<T>): SettingDef<T> => d

export interface DonorTierSetting {
  name: string
  minCents: number
}

export const donorTiersSchema = z
  .array(
    z.object({
      name: z.string().trim().min(1, 'Every tier needs a name').max(40),
      minCents: z.number().int().min(0).max(100_000_000),
    }),
  )
  .min(1)
  .max(8)
  .refine((ts) => ts.some((t) => t.minCents === 0), {
    message: 'One tier must start at $0 so every donor has a place.',
  })
  .refine((ts) => new Set(ts.map((t) => t.minCents)).size === ts.length, {
    message: 'Two tiers start at the same amount.',
  })
  .refine((ts) => new Set(ts.map((t) => t.name.toLowerCase())).size === ts.length, {
    message: 'Two tiers have the same name.',
  })
  // Stored highest first, so the wall and the editor read top-down.
  .transform((ts) => [...ts].sort((a, b) => b.minCents - a.minCents))

export const SETTING_DEFS = {
  'site.banner': def({
    label: 'Site banner',
    help: 'A short announcement shown at the top of every public page.',
    public: true,
    schema: z.object({
      enabled: z.boolean().default(false),
      text: z.string().max(200).default(''),
      link: z.string().max(300).nullable().default(null),
      tone: z.enum(['info', 'warning', 'celebrate']).default('info'),
    }),
    default: { enabled: false, text: '', link: null, tone: 'info' as const },
  }),
  'site.address': def({
    label: 'Mailing address',
    help: 'Shown in the site footer and at the bottom of every newsletter (the law requires a postal address on marketing email).',
    public: true,
    schema: z.string().min(5).max(200),
    default: '200 N Front St, Burlington, IA 52601',
  }),
  'site.contact_email': def({
    label: 'Contact email',
    help: 'Shown in the footer, on the page-not-found page and in error messages. “Report a problem” reports are emailed here.',
    public: true,
    schema: z.string().email(),
    default: 'hello@btownpride.com',
  }),
  'site.branding': def<BrandingSetting>({
    label: 'Branding',
    help: 'Logo, icons and share image. Managed under Admin → Branding.',
    public: true,
    hidden: true,
    schema: brandingSettingSchema,
    default: { assets: {}, iconBackground: '#7c3aed' },
  }),
  'site.social': def({
    label: 'Social links',
    help: 'Footer links. Leave blank to hide.',
    public: true,
    schema: z.object({
      instagram: z.string().max(300).default(''),
      facebook: z.string().max(300).default(''),
      tiktok: z.string().max(300).default(''),
      bluesky: z.string().max(300).default(''),
    }),
    default: { instagram: '', facebook: '', tiktok: '', bluesky: '' },
  }),
  'festival.name': def({
    label: 'Festival name',
    help: 'Used in site copy (home, volunteer and sponsor pages) and as the festival page title before the flagship event is published.',
    public: true,
    schema: z.string().min(1).max(120),
    default: 'Pride on the Riverfront',
  }),
  'festival.date': def({
    label: 'Festival date',
    help: 'YYYY-MM-DD. Drives the home-page countdown and "save the date" until the flagship event is published; after that the event date is used.',
    public: true,
    schema: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    default: '2026-10-03',
  }),
  'payments.fee_cover_default': def({
    label: 'Pre-select "cover processing fees"',
    help: 'Whether the fee-coverage checkbox starts checked at checkout.',
    public: true,
    schema: z.boolean(),
    default: true,
  }),
  'payments.suggested_donation_cents': def({
    label: 'Suggested donation amounts (cents)',
    help: 'Buttons on the donate page.',
    public: true,
    schema: z.array(z.number().int().min(100)).min(1).max(8),
    default: [2500, 5000, 10000, 25000, 50000],
  }),
  'fundraising.donor_tiers': def<DonorTierSetting[]>({
    label: 'Donor wall tiers',
    help: 'The giving levels on the public "Our supporters" page. Each donor is listed under the highest tier their gifts for the year add up to. One tier must start at $0.',
    public: false,
    schema: donorTiersSchema,
    default: [
      { name: 'Riverfront Circle', minCents: 100000 },
      { name: 'Champions', minCents: 50000 },
      { name: 'Allies', minCents: 25000 },
      { name: 'Friends', minCents: 10000 },
      { name: 'Supporters', minCents: 0 },
    ],
  }),
  'fundraising.default_fund': def({
    label: 'Default fund',
    help: 'Code of the fund used for fees, sponsorships, merch and gifts with no fund chosen. Set it under Admin → Campaigns & funds.',
    public: false,
    hidden: true,
    schema: z.string().trim().max(40),
    default: 'general',
  }),
  'fundraising.auto_statements': def({
    label: 'Send year-end giving statements automatically',
    help: "When on, last year's giving statements are emailed to every donor on or after January 15 (each donor once). You can always send them by hand under Admin → Giving statements.",
    public: false,
    schema: z.boolean(),
    default: false,
  }),
  'privacy.discreet_sender_name': def({
    label: 'Sender name for discreet emails',
    help: 'People in discreet mode get email from this name, with neutral subject lines and no logo. Keep it free of "Pride" and the organization name. The sending address itself cannot change.',
    public: false,
    schema: z.string().trim().min(1).max(60),
    default: 'Notifications',
  }),
  'shop.enabled': def({
    label: 'Shop enabled',
    help: 'Hide the storefront (existing orders still work).',
    public: true,
    schema: z.boolean(),
    default: true,
  }),
  'sponsors.materials_due': def({
    label: 'Sponsor materials due date',
    help: 'YYYY-MM-DD. Shown to sponsors in their portal ("Please send materials by …") and on the admin sponsor pages. Blank hides it.',
    public: false,
    schema: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
      .or(z.literal('')),
    default: '',
  }),
  'volunteers.signups_open': def({
    label: 'Volunteer sign-ups open',
    help: 'Turn off to pause all public shift sign-ups.',
    public: true,
    schema: z.boolean(),
    default: true,
  }),
  'volunteers.shift_reminders': def({
    label: 'Volunteer shift reminders',
    help: 'Email each volunteer the evening before their shift (from 6 pm Central) and again about 2 hours before it starts, with an "Add to calendar" link and a link to cancel. Turn off to send neither.',
    public: false,
    schema: z.boolean(),
    default: true,
  }),
  'volunteers.interest_areas': def<string[]>({
    label: 'Volunteer interest areas',
    help: 'The checkboxes on the "Join our volunteer list" form, in order. Renaming an area does not change what people already picked.',
    public: true,
    schema: z.array(z.string().trim().min(1).max(160)).min(1).max(40),
    default: DEFAULT_INTEREST_AREAS,
  }),
  'mutual_aid.enabled': def({
    label: 'Mutual aid requests open',
    help: 'Shows the /mutual-aid page and footer link and accepts requests. Off: the page does not exist and the form refuses submissions.',
    public: true,
    schema: z.boolean(),
    default: false,
  }),
  'mutual_aid.retention_days': def({
    label: 'Mutual aid retention (days)',
    help: 'Closed mutual aid requests older than this are permanently deleted (answers and attachments) by the nightly job. Only a count is kept.',
    public: false,
    schema: z.number().int().min(7).max(3650),
    default: 180,
  }),
  'newsletters.daily_send_limit': def({
    label: 'Newsletter emails per day',
    help: 'The most email the site sends in one day (Central time), counting receipts and other messages as well as newsletters; a newsletter that reaches it continues the next day. Keep it under your Cloudflare Email Service daily quota, leaving room for receipts.',
    public: false,
    schema: z.number().int().min(1).max(100_000),
    default: 300,
  }),
  'newsletters.batch_size': def({
    label: 'Newsletter emails per batch',
    help: 'How many newsletter emails go out every 15 minutes (each in its own message). Up to 100.',
    public: false,
    schema: z.number().int().min(1).max(100),
    default: 50,
  }),
  'notifications.staff_digest_email': def({
    label: 'Staff digest email',
    help: 'Where nightly summaries (new applications, failed syncs) are sent. Blank disables.',
    public: false,
    schema: z.string().email().or(z.literal('')),
    default: '',
  }),
} as const

export type SettingKey = keyof typeof SETTING_DEFS
export type SettingValue<K extends SettingKey> = (typeof SETTING_DEFS)[K]['default']
export const SETTING_KEYS = Object.keys(SETTING_DEFS) as SettingKey[]

export type PublicSettings = { [K in SettingKey]?: SettingValue<K> }
